//@flow
import _ from 'lodash';
import request from 'superagent';
import ApiError from '../entities/ApiError';
import PMTConstants from '../constants/PMTConstants';
import I18n from '../i18n/I18n';

class ApiManager {

  ContentTypes = {
    FORM_URL_ENCODE: 'application/x-www-form-urlencoded'
  };

  /**
   * The default ApiManager configuration.
   * @type {Object}
   */
  config = {

    /**
     * A middleware used in case of error.
     * Must return true if the middleware have taking care of the error.
     * Use to handle 401, 500, etc.
     *
     * @param error
     * @param res If undefined, the request have encounter a connexion error.
     * @type Function
     *
     * Example of middleware:
     *
     * ```
     * errorMiddleware: (error, res, success, failure) => {
     * if (!_.isNil(res)) {
     *       if (res.statusCode === 401) {
     *         console.error('[API] 401, redirect to login');
     *         return true;
     *       }
     *
     *       if (res.statusCode === 500) {
     *         console.log(res);
     *         Logger.error('API 500', res);
     *         // redirect to error 500 page
     *         return true;
     *       }
     *     }
     *     return false;
     *   }
     * ```
     */
    errorMiddleware: (error: Object, res: Object, success: Function,
        failure: Function) => {
      return false;
    },

    /**
     * Function that must return an object.
     *
     * Example:
     * {
     *  Authorization:  'toto',
     *  OtherHeader: 'toto42'
     * }
     *
     * Note that the headers set to the request's params have the priority over the headers given
     * by the middleware.
     *
     * @type Function
     */
    headersMiddleware: () => {
      return {};
    },

    /**
     * Runs before the first send, allowing asynchronous preparation.
     * @type ?Function (send, failure) => void
     */
    requestMiddleware: null,

    /**
     * Return true when handling the response instead of handleResponse.
     * Context contains the sent authorization, retried, retry and failure.
     * @type ?Function (error, res, context) => boolean
     */
    responseMiddleware: null,

    /**
     * @type string
     */
    apiUrl: '',
  };

  configure(configuration: Object) {
    this.config = _.merge(this.config, configuration);
  }

  //
  // ------------- Tools
  //

  getApiUrl(): string {
    return this.config.apiUrl;
  }

  /**
   * Merge headersParam with the headers given by the headersMiddleware.
   * Priority is given to the params headers over the middleware headers.
   */
  getHeaders(headersParam: Object): Object {
    const headersToSet = _.merge(this.config.headersMiddleware(), headersParam);

    return headersToSet;
  }

  /**
   * Format the user credentials for the 'Authorization' header.

   * @param username The user username
   * @param password The user password
   * @param type The type of account: pro, user, ...
   * @returns {string} The string to put as 'Authorization' header on the HTTP request.
   */
  formatAuthorizationHeader(username: string, password: string,
      type: string = 'pro'): string {
    const base64Credentials = btoa(`${type}_${username}:${password}`);
    return `Basic ${base64Credentials}`;
  }

  formatAuthorizationHeaderLight(username: string): string {
    const base64Credentials = btoa(`user_light_${username}:`);
    return `Basic ${base64Credentials}`;
  }

  /**
   * Handle an HTTP response.
   * Calls the success callback in case of success
   * Calls the failure callback in case of error and if the errorMiddleware does not handle the error.
   *
   * @param error
   * @param res
   * @param success the closure called on success. Take a json object as parameter.
   * @param failure the closure called on failure. Take an ApiError as parameter.
   */
  handleResponse(error: Object, res: Object, success: Function,
      failure: Function) {
    if (!_.isUndefined(res) && this.isSuccessResponse(res.statusCode)) {
      if (!_.isEmpty(res.text)) {
        let response;
        try {
          response = JSON.parse(res.text);
        } catch (e) {
          response = res.text;
        }
        success(response);
      } else {
        success();
      }
    } else {
      const errorMiddlewareUsed = this.config.errorMiddleware(error, res,
          success, failure);

      if (!errorMiddlewareUsed) {
        if (_.isUndefined(res)) { // no internet connexion / no response
          const apiError = new ApiError(PMTConstants.Error.NO_INTERNET,
              'No internet connectivity');
          apiError.localizedMessage = I18n.tr('api_error.no_internet');
          failure(apiError);
        } else {
          console.log('get api errors');
          failure(this.createApiError(res.text, res.headers['p-session-id']));
        }
      }
    }
  }

  /**
   * Parse the HTTP response body to create an ApiError object.
   * @param responseBody The HTTP response body.
   * @returns {ApiError} A populate ApiError object.
   */
  createApiError(responseBody: string, sessionId?: string): ApiError {
    const error = new ApiError(PMTConstants.Error.UNKNOWN,
        'Something went wrong, please try again');

    if (!_.isNull(responseBody) && !_.isUndefined(responseBody)) {
      try {
        const json = JSON.parse(responseBody);

        if (json) {
          if (!_.isEmpty(json.code)) {
            error.setCode(json.code);
            error.setMessage(json.message);
            error.setExtra(json.extra);
          }
        }
      } catch (syntaxError) { // SyntaxError exception
        error.setDetail(responseBody);
      }
    }

    if (sessionId) {
      error.setSessionId(sessionId);
    }

    return error;
  }

  /**
   *
   * @param statusCode the response status code
   * @returns {boolean} True if the status code indicate a successful response
   */
  isSuccessResponse(statusCode: number): boolean {
    return statusCode >= 200 && statusCode < 300;
  }

  //
  // ------------- Requests tools for PMT API
  //

  executeRequest(createRequest: Function, success: Function,
      failure: Function) {
    let started = false;
    let retryStarted = false;

    const send = (retried: boolean) => {
      const req = createRequest();
      const authorization = req.get('Authorization');

      req.end((error: Object, res: Object) => {
        const context = {
          authorization,
          retried,
          retry: () => {
            if (!retried && !retryStarted) {
              retryStarted = true;
              send(true);
            }
          },
          failure,
        };

        if (this.config.responseMiddleware &&
            this.config.responseMiddleware(error, res, context)) {
          return;
        }

        this.handleResponse(error, res,
            (json) => {
              success(json);
            },
            (apiError) => {
              failure(apiError);
            }
        );
      });
    };

    const firstSend = () => {
      if (!started) {
        started = true;
        send(false);
      }
    };

    if (this.config.requestMiddleware) {
      this.config.requestMiddleware(firstSend, failure);
    } else {
      firstSend();
    }
  }

  /**
   * Create an HTTP GET request.
   * @param endpoint The API endpoint (see ApiEndpoints).
   * @param query The query use on the url.
   * @param success the closure called on success. Take a json object as parameter.
   * @param failure the closure called on failure. Take an ApiError as parameter.
   */
  get(options: Object) {
    const {endpoint, query, headers, success, failure} = options;

    this.executeRequest(() => request
    .get(`${this.getApiUrl()}${endpoint}`, null, null)
    .query(query)
    .set('Accept', 'application/json')
    .set(this.getHeaders(headers)), success, failure);
  }

  /**
   * Create an HTTP GET request.
   * @param endpoint The API endpoint (see ApiEndpoints).
   * @param query The query use on the url.
   * @param success the closure called on success. Take a json object as parameter.
   * @param failure the closure called on failure. Take an ApiError as parameter.
   */
  delete(options: Object) {
    const {endpoint, query, headers, success, failure} = options;

    this.executeRequest(
        () => request.delete(`${this.getApiUrl()}${endpoint}`, null)
        .query(query)
        .set('Accept', 'application/json')
        .set(this.getHeaders(headers)), success, failure);
  }

  post(options: Object) {
    const {endpoint, data, headers, query, success, failure} = options;

    this.executeRequest(
        () => request.post(`${this.getApiUrl()}${endpoint}`, null, null)
        .type('json')
        .query(query)
        .send(JSON.stringify(data))
        .set('Accept', 'application/json')
        .set(this.getHeaders(headers)), success, failure);
  }

  /**
   * Send a multipart form
   *
   * @param endpoint
   * @param data: example: { otherParameters: JSON.stringify(entity) }
   * @param files: example: { imageCover: // png file here // }
   * @param success
   * @param failure
   */
  postMultipart(options: Object) {
    const {endpoint, data, files, headers, query, success, failure} = options;

    this.executeRequest(() => {
      const req = request.post(`${this.getApiUrl()}${endpoint}`, null, null)
      .query(query);

      for (const key in data) {
        if (data.hasOwnProperty(key)) {
          req.field(key, data[key]);
        }
      }

      for (const key in files) {
        if (files.hasOwnProperty(key)) {
          console.log('put multipart, attach file', key);
          req.attach(key, files[key]);
        }
      }

      return req.set('Accept', 'application/json')
      .set(this.getHeaders(headers));
    }, success, failure);
  }

  /**
   * Create an HTTP PUT request.
   * Note this function will use the 'json' (application/json) format instead of the 'form' format
   * (application/x-www-form-urlencoded)
   *
   * See: https://visionmedia.github.io/superagent/#post-/-put-requests
   *
   * @param endpoint The API endpoint (see ApiEndpoints).
   * @param data The data to send.
   * @param success the closure called on success. Take a json object as parameter.
   * @param failure the closure called on failure. Take an ApiError as parameter.
   */
  put(options: Object) {
    const {endpoint, data, headers, query, success, failure} = options;

    this.executeRequest(
        () => request.put(`${this.getApiUrl()}${endpoint}`, null, null)
        .type('json')
        .send(JSON.stringify(data))
        .query(query)
        .set('Accept', 'application/json')
        .set(this.getHeaders(headers)), success, failure);
  }

  /**
   * Send a multipart form
   *
   * @param endpoint
   * @param data - example: { otherParameters: JSON.stringify(entity) }
   * @param files - example: { imageCover: // png file here // }
   * @param success
   * @param failure
   */
  putMultipart(options: Object) {
    const {endpoint, data, files, headers, query, success, failure} = options;

    this.executeRequest(() => {
      const req = request.put(`${this.getApiUrl()}${endpoint}`, null, null)
      .query(query);

      for (const key in data) {
        if (data.hasOwnProperty(key)) {
          req.field(key, data[key]);
        }
      }

      console.log('[put multipart] files', files);
      for (const key in files) {
        if (files.hasOwnProperty(key)) {
          const file = files[key];

          // handle file keys with '.' in it.
          if (!_.isUndefined(file) && !_.isNull(file)) {
            console.log('[put multipart] attach file', key);
            req.attach(key, file);
          } else {
            console.log('[put multipart] could not attach file', key);
          }
        }
      }

      return req.set('Accept', 'application/json')
      .set(this.getHeaders(headers));
    }, success, failure);
  }

//
// Requests tools for extern APIs
//

  /**
   * Create an HTTP GET request.
   */
  getExtern(options: Object) {
    const {url, query, headers, success, failure} = options;

    request
    .get(options.url)
    .query(query)
    .set(headers)
    .end((error: Object, res: Object) => {
      if (!_.isNil(res) && this.isSuccessResponse(res.statusCode)) {
        success(res);
      } else {
        failure(error, res);
      }
    });
  }

  /**
   * Create an HTTP GET request.
   */
  postExtern(options: Object) {
    const {url, data, headers, success, failure} = options;

    request
    .post(options.url)
    .send(data)
    .set(headers)
    .end((error: Object, res: Object) => {
      if (!_.isNil(res) && this.isSuccessResponse(res.statusCode)) {
        success(res);
      } else {
        failure(error, res);
      }
    });
  }

}

let _apiManagerInstance = new ApiManager();

export default _apiManagerInstance;
